/**
 * Brand settings (Settings → Brand), rebuilt 2026-09-04.
 *
 * Three cards, in the order a new admin thinks about them:
 *
 *   1. Identity — logo, company name, two colours. The logo is shown on
 *      a light AND a dark chip because the trailer slides are dark and
 *      the share page is light; "looks fine here" used to mean nothing.
 *   2. Slide look — the theme picker (flag slide_themes). Every card is
 *      a REAL render from the trailer engine with this company's saved
 *      brand, so preview equals render by construction. Intro / Agenda /
 *      Chapter / Outro tabs re-render the gallery for that slide type.
 *      With the flag off only the default card shows (no picker).
 *   3. Your own backgrounds — the existing custom-background uploader
 *      and zone editor, folded into an "advanced" disclosure. Those
 *      controls keep living in App.jsx (the zone editor is a modal with
 *      a lot of state); this component just gives them a home.
 *
 * A sticky save bar shows dirty state so nobody wonders whether their
 * click registered (the transient toast was the #1 beta complaint).
 *
 * Previews are fetched ONE AT A TIME: each themed preview spins up a
 * headless Chromium on the backend, and four in parallel per click was
 * the fastest way to make the settings page feel broken. Results are
 * cached per (theme, slide, saved-brand version) and revoked when the
 * brand is saved, so the gallery refreshes with the new brand.
 *
 * Theming: --room-* tokens only, light hex fallbacks (CLAUDE.md rule).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './BrandSettings.css';

const SLIDE_TABS = [
  { id: 'intro', label: 'Intro' },
  { id: 'agenda', label: 'Agenda' },
  { id: 'moment', label: 'Chapter' },
  { id: 'outro', label: 'Outro' },
];

// The engine's built-in look. Always first; id null = companies.slide_theme NULL.
const DEFAULT_THEME = {
  id: null,
  name: 'TrailerCast default',
  description: 'The standard dark slides, tinted with your colours and carrying your logo.',
  mode: 'dark',
};

const HEX_RE = /^#[0-9a-f]{6}$/i;
const normHex = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

function isDirty(form, saved) {
  const s = saved || {};
  return (form.companyName || '') !== (s.companyName || '')
    || normHex(form.primaryColor) !== normHex(s.primaryColor || '#6366f1')
    || normHex(form.secondaryColor) !== normHex(s.secondaryColor || '#8b5cf6')
    || (form.logoUrl || null) !== (s.logoUrl || null)
    || (form.slideTheme || null) !== (s.slideTheme || null);
}

export default function BrandSettings({
  apiBase,
  authFetch,
  brandForm,
  savedBrand,
  onChange,
  onSave,
  saving,
  error,
  onLogoSelect,
  onRemoveLogo,
  companyLabel,
  themesEnabled,
  brandVersion,
  customBackgrounds,
  customBackgroundCount = 0,
}) {
  const dirty = useMemo(() => isDirty(brandForm, savedBrand), [brandForm, savedBrand]);
  const [savedFlash, setSavedFlash] = useState(false);
  const prevSavingRef = useRef(saving);
  useEffect(() => {
    // saving true → false with no error = a successful save; flash "Saved".
    if (prevSavingRef.current && !saving && !error) {
      setSavedFlash(true);
      const t = setTimeout(() => setSavedFlash(false), 2600);
      return () => clearTimeout(t);
    }
    prevSavingRef.current = saving;
    return undefined;
  }, [saving, error]);
  useEffect(() => { prevSavingRef.current = saving; }, [saving]);

  // ── Themes ──────────────────────────────────────────────────────
  const [themes, setThemes] = useState(null); // null = loading, [] = none
  useEffect(() => {
    if (!themesEnabled) { setThemes([]); return undefined; }
    let dead = false;
    (async () => {
      try {
        const res = await authFetch(`${apiBase}/api/brand/themes`);
        const data = res.ok ? await res.json() : { themes: [] };
        if (!dead) setThemes(Array.isArray(data.themes) ? data.themes : []);
      } catch {
        if (!dead) setThemes([]);
      }
    })();
    return () => { dead = true; };
  }, [apiBase, authFetch, themesEnabled]);

  const cards = useMemo(() => [DEFAULT_THEME, ...((themesEnabled && themes) || [])], [themes, themesEnabled]);
  const selected = brandForm.slideTheme || null;

  // ── Previews (sequential, cached, revoked on brand change) ─────
  const [slideTab, setSlideTab] = useState('intro');
  const [previews, setPreviews] = useState({}); // key → objectURL | 'error'
  const previewsRef = useRef({});
  const keyFor = (themeId, slide) => `${themeId || 'default'}:${slide}:${brandVersion}`;

  useEffect(() => {
    // New saved brand → everything cached is stale. Revoke and reset.
    Object.values(previewsRef.current).forEach((u) => {
      if (u && u !== 'error') { try { URL.revokeObjectURL(u); } catch { /* noop */ } }
    });
    previewsRef.current = {};
    setPreviews({});
  }, [brandVersion]);

  useEffect(() => {
    if (themes === null && themesEnabled) return undefined; // wait for the list
    let dead = false;
    (async () => {
      for (const card of cards) {
        const key = keyFor(card.id, slideTab);
        if (previewsRef.current[key]) continue;
        if (dead) return;
        // Reserve the slot so a re-run doesn't double-fetch it.
        previewsRef.current[key] = 'loading';
        const qs = new URLSearchParams({ slide: slideTab });
        if (themesEnabled) qs.set('theme', card.id || 'default');
        let value = 'error';
        try {
          const res = await authFetch(`${apiBase}/api/brand/slide-preview?${qs.toString()}`);
          if (res.ok) value = URL.createObjectURL(await res.blob());
        } catch { /* value stays 'error' */ }
        if (dead) {
          if (value !== 'error') { try { URL.revokeObjectURL(value); } catch { /* noop */ } }
          delete previewsRef.current[key];
          return;
        }
        previewsRef.current[key] = value;
        setPreviews({ ...previewsRef.current });
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, slideTab, brandVersion, themesEnabled, themes]);

  const retryPreview = (themeId) => {
    const key = keyFor(themeId, slideTab);
    delete previewsRef.current[key];
    setPreviews({ ...previewsRef.current });
  };

  // ── Logo drop zone ─────────────────────────────────────────────
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) onLogoSelect(f);
  }, [onLogoSelect]);

  const setHex = (field) => (e) => {
    let v = e.target.value.trim();
    if (v && !v.startsWith('#')) v = `#${v}`;
    onChange({ [field]: v });
  };
  const primaryOk = HEX_RE.test(brandForm.primaryColor || '');
  const secondaryOk = HEX_RE.test(brandForm.secondaryColor || '');

  return (
    <form className="bs" onSubmit={onSave}>
      <header className="bs-head">
        <div>
          <h2 className="bs-title">Brand</h2>
          <p className="bs-lede">
            What every trailer, share page and follow-up email from <strong>{companyLabel}</strong> looks like.
            Saved changes apply to trailers built from now on; existing videos keep the brand they were built with.
          </p>
        </div>
      </header>

      {/* ── 1. Identity ─────────────────────────────────────────── */}
      <section className="bs-card">
        <div className="bs-card-head">
          <h3>Identity</h3>
          <p>Logo, name and two colours. Everything else is derived from these.</p>
        </div>
        <div className="bs-identity">
          <div className="bs-logo-col">
            <span className="bs-label">Logo</span>
            <div
              className={`bs-drop ${dragOver ? 'is-over' : ''} ${brandForm.logoUrl ? 'has-logo' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current && fileInputRef.current.click(); } }}
              aria-label={brandForm.logoUrl ? 'Change logo' : 'Upload logo'}
            >
              {brandForm.logoUrl ? (
                <div className="bs-logo-chips" aria-hidden="true">
                  <div className="bs-chip bs-chip--light"><img src={brandForm.logoUrl} alt="" /></div>
                  <div className="bs-chip bs-chip--dark"><img src={brandForm.logoUrl} alt="" /></div>
                </div>
              ) : (
                <div className="bs-drop-empty">
                  <span className="bs-drop-icon" aria-hidden="true">⬆</span>
                  <strong>Drop your logo here</strong>
                  <span>or click to choose a file</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onLogoSelect(f); e.target.value = ''; }}
              />
            </div>
            <div className="bs-logo-meta">
              <span>PNG with a transparent background, at least 512px wide. Shown on light and dark surfaces above exactly as it will render.</span>
              {brandForm.logoUrl && (
                <button type="button" className="bs-link bs-link--danger" onClick={onRemoveLogo}>Remove logo</button>
              )}
            </div>
          </div>

          <div className="bs-fields-col">
            <label className="bs-field">
              <span className="bs-label">Company name</span>
              <input
                type="text"
                value={brandForm.companyName}
                placeholder="Acme Corp"
                onChange={(e) => onChange({ companyName: e.target.value })}
              />
            </label>

            <div className="bs-colors">
              <ColorField
                label="Primary colour"
                value={brandForm.primaryColor}
                valid={primaryOk}
                onPick={(v) => onChange({ primaryColor: v })}
                onType={setHex('primaryColor')}
                placeholder="#6366f1"
              />
              <ColorField
                label="Secondary colour"
                value={brandForm.secondaryColor}
                valid={secondaryOk}
                onPick={(v) => onChange({ secondaryColor: v })}
                onType={setHex('secondaryColor')}
                placeholder="#8b5cf6"
              />
              <button
                type="button"
                className="bs-swap"
                title="Swap primary and secondary"
                onClick={() => onChange({ primaryColor: brandForm.secondaryColor, secondaryColor: brandForm.primaryColor })}
              >
                ⇄
              </button>
            </div>

            <div
              className="bs-palette"
              aria-hidden="true"
              style={{ '--bs-p': primaryOk ? brandForm.primaryColor : '#94a3b8', '--bs-s': secondaryOk ? brandForm.secondaryColor : '#94a3b8' }}
            >
              <span className="bs-palette-p" />
              <span className="bs-palette-s" />
              <span className="bs-palette-gradient" />
              <em>How the pair reads as an accent and as a gradient.</em>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Slide look ───────────────────────────────────────── */}
      <section className="bs-card">
        <div className="bs-card-head bs-card-head--row">
          <div>
            <h3>Slide look</h3>
            <p>
              {themesEnabled
                ? 'Pick the design your trailer slides use. Every preview is a real render with your saved brand.'
                : 'How your intro slide renders with your saved brand. Rendered by the trailer engine itself.'}
            </p>
          </div>
          <div className="bs-slide-tabs" role="tablist" aria-label="Slide type">
            {SLIDE_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={slideTab === t.id}
                className={`bs-slide-tab ${slideTab === t.id ? 'is-active' : ''}`}
                onClick={() => setSlideTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {dirty && (
          <p className="bs-note">Previews use your <strong>saved</strong> brand. Save to see these with your new colours or logo.</p>
        )}

        <div className={`bs-themes ${cards.length === 1 ? 'bs-themes--single' : ''}`} role={themesEnabled ? 'radiogroup' : undefined} aria-label={themesEnabled ? 'Slide theme' : undefined}>
          {cards.map((card) => {
            const key = keyFor(card.id, slideTab);
            const state = previews[key];
            const isSelected = (card.id || null) === selected;
            const Tag = themesEnabled ? 'button' : 'div';
            return (
              <Tag
                key={card.id || 'default'}
                type={themesEnabled ? 'button' : undefined}
                role={themesEnabled ? 'radio' : undefined}
                aria-checked={themesEnabled ? isSelected : undefined}
                className={`bs-theme ${isSelected && themesEnabled ? 'is-selected' : ''}`}
                onClick={themesEnabled ? () => onChange({ slideTheme: card.id }) : undefined}
                data-theme-id={card.id || 'default'}
              >
                <div className={`bs-theme-frame bs-theme-frame--${card.mode || 'dark'}`}>
                  {state && state !== 'error' && state !== 'loading' ? (
                    <img src={state} alt={`${card.name} — ${SLIDE_TABS.find((t) => t.id === slideTab).label} slide`} />
                  ) : state === 'error' ? (
                    <div className="bs-theme-error">
                      <span>Couldn’t render this preview.</span>
                      <button type="button" className="bs-link" onClick={(e) => { e.stopPropagation(); retryPreview(card.id); }}>Retry</button>
                    </div>
                  ) : (
                    <div className="bs-theme-skeleton" aria-label="Rendering preview">
                      <span /><span /><span />
                    </div>
                  )}
                </div>
                <div className="bs-theme-meta">
                  <div className="bs-theme-name">
                    <strong>{card.name}</strong>
                    {isSelected && themesEnabled && <span className="bs-theme-badge">Selected</span>}
                    {!themesEnabled && <span className="bs-theme-badge bs-theme-badge--muted">Current</span>}
                  </div>
                  <span className="bs-theme-desc">{card.description}</span>
                </div>
              </Tag>
            );
          })}
        </div>
        {themesEnabled && customBackgroundCount > 0 && (
          <p className="bs-note bs-note--warn">
            {selected
              ? <>You have {customBackgroundCount} custom background{customBackgroundCount === 1 ? '' : 's'} uploaded below. They are <strong>not used</strong> while a theme is selected. Choose TrailerCast default to use them again.</>
              : <>You have {customBackgroundCount} custom background{customBackgroundCount === 1 ? '' : 's'} uploaded below. With TrailerCast default selected, those slide types render your uploaded image.</>}
          </p>
        )}
      </section>

      {/* ── 3. Your own backgrounds (advanced) ─────────────────── */}
      <details className="bs-card bs-advanced" open={customBackgroundCount > 0}>
        <summary>
          <div>
            <h3>Your own slide backgrounds</h3>
            <p>Advanced. Upload finished 1920×1080 images per slide type; the AI text is placed on top.</p>
          </div>
          <span className="bs-advanced-chevron" aria-hidden="true">▾</span>
        </summary>
        <div className="bs-advanced-body">
          <details className="bs-help">
            <summary>Exporting from PowerPoint, Keynote, Google Slides, Canva or Figma</summary>
            <ul>
              <li><strong>PowerPoint:</strong> File → Export → Change File Type → PNG, “Just This One”.</li>
              <li><strong>Keynote:</strong> File → Export To → Images, format PNG, pick the slide range.</li>
              <li><strong>Google Slides:</strong> File → Download → PNG image (current slide).</li>
              <li><strong>Canva / Figma:</strong> export the 1920×1080 frame as PNG.</li>
            </ul>
            <p>Leave empty space where the text should go, then use <strong>Edit zones</strong> to place it exactly. Without zones, TrailerCast finds the clearest area automatically.</p>
          </details>
          {customBackgrounds}
        </div>
      </details>

      {error && (
        <div className="error-message" role="alert"><span /> {error}</div>
      )}

      {/* ── Sticky save bar ─────────────────────────────────────────
          Last in the DOM so it pins to the viewport bottom while the
          page scrolls and settles at the true end of the form. */}
      <div className={`bs-savebar ${dirty ? 'is-dirty' : ''}`}>
        <span className="bs-savebar-state" aria-live="polite">
          {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedFlash ? 'Saved ✓' : 'All changes saved'}
        </span>
        <button type="submit" className="bs-save" disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save brand'}
        </button>
      </div>
    </form>
  );
}

function ColorField({ label, value, valid, onPick, onType, placeholder }) {
  return (
    <div className="bs-field bs-color">
      <span className="bs-label">{label}</span>
      <div className={`bs-color-row ${valid ? '' : 'is-invalid'}`}>
        <input
          type="color"
          aria-label={`${label} picker`}
          value={valid ? value : '#94a3b8'}
          onChange={(e) => onPick(e.target.value)}
        />
        <input
          type="text"
          className="bs-hex"
          value={value || ''}
          maxLength={7}
          placeholder={placeholder}
          onChange={onType}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          aria-label={`${label} hex`}
        />
      </div>
    </div>
  );
}
